<template>
  <div class="tags-row">
    <template v-if="loading">
      <span class="skel skel--chip"></span>
      <span class="skel skel--chip"></span>
      <span class="skel skel--chip"></span>
    </template>
    <template v-else>
      <span
        v-for="tag in tags"
        :key="'TG' + tag.id"
        class="tag-chip text-white"
        :class="{ 'tag-chip--archived': tag.is_archived }"
        :style="tagChipStyle(tag.color)"
      >
        {{ tag.name }}
        <button
          v-if="!readonly"
          class="tag-chip__remove"
          type="button"
          :disabled="saving"
          :aria-label="'Remove tag ' + tag.name"
          @click.stop="$emit('remove', tag)"
        >
          <i class="tim-icons icon-simple-remove"></i>
        </button>
      </span>
      <span v-if="readonly && tags.length === 0" class="tag-picker__none">{{ emptyText }}</span>
      <el-dropdown v-if="!readonly" trigger="click" @command="onAdd">
        <span class="tag-chip tag-chip--add" @click.stop>
          <i v-if="saving" class="el-icon-loading mr-1" style="color: #e14eca"></i>
          <i v-else class="tim-icons icon-simple-add mr-1"></i>
          {{ addLabel }}
        </span>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item v-for="tag in availableTags" :key="'AT' + tag.id" :command="tag.id">
            <span class="tag-chip__dot mr-1" :style="{ backgroundColor: tag.color }"></span>
            {{ tag.name }}
          </el-dropdown-item>
          <el-dropdown-item v-if="availableTags.length === 0" disabled>No more tags</el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { tagChipStyle } from '@/util/tagChip'

interface Tag { id: number; name: string; color: string; is_archived?: number | boolean }

const props = withDefaults(defineProps<{
  tags: Tag[]
  catalog: Tag[]
  saving?: boolean
  readonly?: boolean
  loading?: boolean
  addLabel?: string
  emptyText?: string
}>(), {
  saving: false,
  readonly: false,
  loading: false,
  addLabel: 'Add tag',
  emptyText: 'No tags'
})

const emit = defineEmits<{
  (e: 'add', tagId: number): void
  (e: 'remove', tag: Tag): void
}>()

const availableTags = computed(() => {
  const applied = new Set((props.tags || []).map(t => t.id))
  return (props.catalog || []).filter(t => !applied.has(t.id) && !t.is_archived)
})

function onAdd(tagId: number) {
  if (props.saving) return
  emit('add', Number(tagId))
}
</script>

<script lang="ts">
// Tag chips with an inline remove plus an Add tag picker of the remaining
// library tags. The same pattern the booking drawer and tracker steps carry
// inline; new consumers use this component instead of a fresh copy.
import { Dropdown, DropdownMenu, DropdownItem } from 'element-ui'

export default {
  name: 'tag-picker',
  components: {
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem
  }
}
</script>

<style scoped>
.tag-picker__none {
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.4);
}
.white-content .tag-picker__none {
  color: rgba(52, 70, 117, 0.6);
}
</style>
